import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError, from, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ActivityType } from './activity-type.model';
import { Page } from '../../models/page-wrapper.model';
import { environment } from '../../../environments/environment';
import { PickerRecentCacheService } from '../../shared/services/picker-recent-cache.service';
import { EntityDropdownPickerAdapter } from '../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { PickerRecentRecord } from '../../shared/components/entity-dropdown-picker/picker-recent.model';

@Injectable({
  providedIn: 'root',
})
export class ActivityTypeService {
  private apiUrl = `${environment.restApiUrl}masterdata/activity-types`;
  private readonly pickerEntityKey = 'activity-type';

  constructor(
    private http: HttpClient,
    private recentPickerCache: PickerRecentCacheService
  ) {}

  /**
   * Get all activity types with pagination and filtering.
   * @param params HttpParams for pagination, sorting, and QueryDSL filtering.
   */
  getAllActivityTypes(params: any): Observable<Page<ActivityType>> {
    return this.http.get<Page<ActivityType>>(this.apiUrl, { params });
  }

  searchActivityTypes(query: string, page = 0, size = 25): Observable<Page<ActivityType>> {
    return forkJoin([
      this.searchActivityTypesByField('name', query, page, size),
      this.searchActivityTypesByField('code', query, page, size),
    ]).pipe(
      map(([namePage, codePage]) => this.mergeActivityTypePages(namePage, codePage, page, size)),
      catchError(this.handleError)
    );
  }

  /**
   * Get a single activity type by its ID.
   * @param id The ID of the activity type.
   */
  getActivityTypeById(id: number): Observable<ActivityType> {
    return this.http.get<ActivityType>(`${this.apiUrl}/${id}`);
  }

  loadRecentActivityTypes(limit = 10): Observable<ActivityType[]> {
    return from(this.recentPickerCache.loadRecent<ActivityType>(this.pickerEntityKey, limit));
  }

  getFreshActivityTypeById(id: number | string): Observable<ActivityType> {
    return this.getActivityTypeById(Number(id));
  }

  rememberActivityType(activityType: ActivityType): Observable<void> {
    const record: PickerRecentRecord<ActivityType> = {
      key: `${this.pickerEntityKey}::${activityType.id}`,
      entityKey: this.pickerEntityKey,
      userKey: this.getCurrentUserKey(),
      entityId: activityType.id,
      label: `${activityType.name ?? ''}${activityType.code ? ` - ${activityType.code}` : ''}`.trim(),
      payload: activityType,
      lastUsedAt: Date.now(),
    };

    return from(this.recentPickerCache.remember(record));
  }

  createActivityTypePickerAdapter(): EntityDropdownPickerAdapter<ActivityType> {
    return {
      entityKey: this.pickerEntityKey,
      loadRecent: (limit?: number) => this.loadRecentActivityTypes(limit),
      search: (query: string, page: number, size: number) => this.searchActivityTypes(query, page, size),
      getFreshById: (id: number | string) => this.getFreshActivityTypeById(id),
      rememberSelection: (activityType: ActivityType) => this.rememberActivityType(activityType),
      getId: (activityType: ActivityType) => activityType.id,
      getLabel: (activityType: ActivityType) => `${activityType.name ?? activityType.code ?? activityType.id ?? ''}${activityType.code && activityType.name ? ` - ${activityType.code}` : ''}`.trim(),
      getSecondaryLabel: () => '',
      filterBy: 'name,code',
    };
  }

  createActivityType(activityType: Partial<ActivityType>): Observable<ActivityType> {
    return this.http.post<ActivityType>(this.apiUrl, activityType).pipe(catchError(this.handleError));
  }

  updateActivityType(activityType: Partial<ActivityType>): Observable<ActivityType> {
    return this.http
      .put<ActivityType>(`${this.apiUrl}/${activityType.id}`, activityType)
      .pipe(catchError(this.handleError));
  }

  deleteActivityType(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`)
      .pipe(catchError(this.handleError));
  }
  
    private handleError(error: any): Observable<never> {
    console.error('An error occurred in ActivtyTypeService:', error);
    return throwError(() => new Error('Something bad happened; please try again later.'));
  }

  private getCurrentUserKey(): string {
    try {
      const rawUser = sessionStorage.getItem('appUser');
      if (!rawUser) {
        return 'anonymous';
      }

      const parsed = JSON.parse(rawUser);
      return String(parsed?.id ?? 'anonymous');
    } catch {
      return 'anonymous';
    }
  }

  private searchActivityTypesByField(
    field: 'name' | 'code',
    query: string,
    page: number,
    size: number
  ): Observable<Page<ActivityType>> {
    const params = {
      [field]: query,
      page,
      size,
    };

    return this.http.get<Page<ActivityType>>(this.apiUrl, { params }).pipe(
      catchError(() => of(this.emptyActivityTypePage(page, size)))
    );
  }

  private mergeActivityTypePages(
    firstPage: Page<ActivityType>,
    secondPage: Page<ActivityType>,
    page: number,
    size: number
  ): Page<ActivityType> {
    const merged = new Map<number, ActivityType>();
    [...(firstPage.content || []), ...(secondPage.content || [])].forEach((activityType) => {
      if (activityType?.id !== undefined && activityType?.id !== null) {
        merged.set(activityType.id, activityType);
      }
    });

    const content = Array.from(merged.values()).slice(0, size);

    return {
      ...firstPage,
      content,
      number: page,
      size,
      totalElements: merged.size,
      numberOfElements: content.length,
    };
  }

  private emptyActivityTypePage(page: number, size: number): Page<ActivityType> {
    return {
      content: [],
      pageable: {
        pageNumber: page,
        pageSize: size,
        sort: { sorted: false, unsorted: true, empty: true },
        offset: page * size,
        paged: true,
        unpaged: false,
      },
      last: true,
      totalPages: 0,
      totalElements: 0,
      size,
      number: page,
      sort: { sorted: false, unsorted: true, empty: true },
      first: page === 0,
      numberOfElements: 0,
      empty: true,
    };
  }

}
